import { useState, useCallback, useRef, useLayoutEffect, useEffect } from "react";
import type { Token } from "@/types";
import { TokenAvatar } from "./TokenAvatar";
import { ChevronIcon, ArrowUpDownIcon } from "./Icons";
import { BalanceSkeleton } from "./Skeleton";
import { GaslessFeeDisplay } from "./GaslessFeeDisplay";
import { fmtNumber, fmtUsd } from "@/lib/utils";

interface SendPanelProps {
	token: Token | undefined;
	tokens: Token[];
	amount: string;
	recipient: string;
	isBalanceLoading: boolean;
	isFeeLoading: boolean;
	feeSol: number | undefined;
	walletConnected: boolean;
	onAmountChange: (amount: string) => void;
	onPickToken: () => void;
	onPickRecipient: () => void;
	onSubmit: () => void;
}

export function SendPanel({
	token,
	amount,
	recipient,
	isBalanceLoading,
	isFeeLoading,
	feeSol,
	walletConnected,
	onAmountChange,
	onPickToken,
	onPickRecipient,
	onSubmit,
}: SendPanelProps) {
	const [inputMode, setInputMode] = useState<"usd" | "token">("usd");
	// Pure number string (no prefix/suffix)
	const [rawDisplay, setRawDisplay] = useState("");
	// Where to restore the cursor after a value update
	const cursorPos = useRef<number | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const decimals = token?.decimals ?? 9;
	const tokAmt = parseFloat(amount) || 0;
	const usdAmt = token ? tokAmt * token.usd : 0;

	// Prefix/suffix baked into the input value
	const prefix = inputMode === "usd" ? "$" : "";
	const suffix = inputMode === "token" && token ? ` ${token.symbol}` : "";
	const prefixLen = prefix.length;
	const suffixLen = suffix.length;

	// Full string shown in the input: "$21" or "0.02 SOL"
	const inputValue = rawDisplay ? `${prefix}${rawDisplay}${suffix}` : "";

	useEffect(() => {
		if (amount === "") {
			setRawDisplay("");
			cursorPos.current = null;
		}
	}, [amount]);

	// After every render, restore cursor to where we want it
	useLayoutEffect(() => {
		const inp = inputRef.current;
		if (inp && cursorPos.current !== null) {
			inp.setSelectionRange(cursorPos.current, cursorPos.current);
			cursorPos.current = null;
		}
	});

	// Clamp cursor into the editable zone [prefixLen .. len-suffixLen]
	const clampCursor = useCallback(
		(inp: HTMLInputElement) => {
			const len = inp.value.length;
			const end = len - suffixLen;
			const s = Math.max(prefixLen, Math.min(inp.selectionStart ?? prefixLen, end));
			const e = Math.max(prefixLen, Math.min(inp.selectionEnd ?? prefixLen, end));
			if (s !== inp.selectionStart || e !== inp.selectionEnd) {
				inp.setSelectionRange(s, e);
			}
		},
		[prefixLen, suffixLen],
	);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const raw = e.target.value;

			// Strip prefix and suffix to isolate the number part
			let num = raw;
			if (prefix && num.startsWith(prefix)) num = num.slice(prefixLen);
			if (suffix) {
				const idx = num.lastIndexOf(suffix.trim());
				if (idx !== -1) num = num.slice(0, idx).replace(/\s+$/, "");
			}

			if (inputMode === "usd") {
				const digits = num.replace(/\D/g, "");
				const nextVal = `${prefix}${digits}${suffix}`;
				cursorPos.current = nextVal.length - suffixLen;
				setRawDisplay(digits);
				const n = parseInt(digits, 10) || 0;
				const tok = token && token.usd > 0 ? n / token.usd : 0;
				onAmountChange(tok ? String(tok) : "");
			} else {
				let v = num.replace(/[^0-9.]/g, "");
				const dotIdx = v.indexOf(".");
				if (dotIdx !== -1) {
					const intPart = v.slice(0, dotIdx);
					const fracPart = v.slice(dotIdx + 1).replace(/\./g, "").slice(0, decimals);
					v = `${intPart}.${fracPart}`;
				}
				const nextVal = `${prefix}${v}${suffix}`;
				cursorPos.current = nextVal.length - suffixLen;
				setRawDisplay(v);
				onAmountChange(v);
			}
		},
		[inputMode, token, decimals, onAmountChange, prefix, suffix, prefixLen, suffixLen],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			const inp = e.currentTarget;
			const sel = inp.selectionStart ?? prefixLen;
			const selEnd = inp.selectionEnd ?? prefixLen;
			const len = inp.value.length;
			const end = len - suffixLen;

			// Block backspace that would eat the prefix
			if (e.key === "Backspace" && sel <= prefixLen && selEnd <= prefixLen) {
				e.preventDefault();
				return;
			}
			// Block Delete that would eat the suffix
			if (e.key === "Delete" && sel >= end && selEnd >= end) {
				e.preventDefault();
				return;
			}
			// Block dot in USD mode
			if (e.key === "." && inputMode === "usd") {
				e.preventDefault();
				return;
			}
		},
		[inputMode, prefixLen, suffixLen],
	);

	const setQuick = (usd: number) => {
		if (!token || token.usd <= 0) return;
		const tok = usd / token.usd;
		setRawDisplay(String(usd));
		setInputMode("usd");
		onAmountChange(String(tok));
	};

	const switchMode = () => {
		const next = inputMode === "usd" ? "token" : "usd";
		setInputMode(next);
		if (next === "usd") {
			const dollars = Math.round(usdAmt);
			setRawDisplay(dollars > 0 ? String(dollars) : "");
		} else {
			setRawDisplay(tokAmt > 0 ? String(tokAmt) : "");
		}
		setTimeout(() => inputRef.current?.focus(), 0);
	};

	const counterText =
		inputMode === "usd"
			? tokAmt > 0
				? `${fmtNumber(tokAmt, decimals)} ${token?.symbol ?? ""}`
				: token?.symbol ?? ""
			: fmtUsd(usdAmt);

	const hasInsufficientBalance = token && tokAmt > 0 && tokAmt > token.balance;
	const isDisabled =
		!walletConnected ||
		!token ||
		tokAmt <= 0 ||
		!recipient ||
		Boolean(hasInsufficientBalance);

	const btnLabel = () => {
		if (!walletConnected) return "Connect wallet to send";
		if (!token || tokAmt <= 0) return "Enter an amount";
		if (!recipient) return "Paste a recipient address";
		if (hasInsufficientBalance) return "Insufficient balance";
		return "Send";
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
			<div className="sendbuy-card">
				<div className="sendbuy-label">Enter an amount</div>
				<div className="sendbuy-amount">
					<input
						ref={inputRef}
						className="sendbuy-input"
						inputMode={inputMode === "usd" ? "numeric" : "decimal"}
						placeholder={`${prefix}0${suffix}`}
						value={inputValue}
						onChange={handleChange}
						onKeyDown={handleKeyDown}
						onClick={(e) => clampCursor(e.currentTarget)}
						onSelect={(e) => clampCursor(e.currentTarget as HTMLInputElement)}
						onFocus={(e) => {
							// On focus, place cursor at end of editable region
							const inp = e.currentTarget;
							const end = inp.value.length - suffixLen;
							inp.setSelectionRange(end, end);
						}}
					/>
				</div>
				<div className="sendbuy-counter">
					<span>{counterText}</span>
					<button
						className="sendbuy-flip"
						aria-label="Toggle USD/token input"
						onClick={switchMode}
					>
						<ArrowUpDownIcon />
					</button>
				</div>

				{token ? (
					<button className="sendbuy-token" onClick={onPickToken}>
						<TokenAvatar token={token} size={28} />
						<span className="token-meta">
							<span className="token-sym">{token.symbol}</span>
							<span className="token-net">Solana</span>
						</span>
						<ChevronIcon style={{ color: "var(--text-muted)" }} />
					</button>
				) : (
					<button className="sendbuy-token" onClick={onPickToken}>
						<span
							style={{
								width: 28,
								height: 28,
								borderRadius: "50%",
								background: "var(--surface-2)",
								border: "1px solid var(--border)",
								display: "inline-block",
							}}
						/>
						<span className="token-meta">
							<span className="token-sym">Select token</span>
						</span>
						<ChevronIcon style={{ color: "var(--text-muted)" }} />
					</button>
				)}

				<div className="sendbuy-quick">
					{[10, 50, 200].map((u) => (
						<button key={u} className="sendbuy-quick-chip" onClick={() => setQuick(u)}>
							${u}
						</button>
					))}
					{token && walletConnected && (
						<button
							className="sendbuy-quick-chip"
							onClick={() => {
								const bal = token.balance;
								setRawDisplay(
									inputMode === "usd"
										? String(Math.round(bal * token.usd))
										: String(bal),
								);
								onAmountChange(String(bal));
							}}
						>
							MAX
						</button>
					)}
				</div>
			</div>

			<div className="send-meta">
				<div className="send-meta-row">
					<span className="label">Balance</span>
					<span>
						{isBalanceLoading ? (
							<BalanceSkeleton />
						) : token && walletConnected ? (
							`${fmtNumber(token.balance, 4)} ${token.symbol}`
						) : (
							"—"
						)}
					</span>
				</div>

				<div className="send-meta-row">
					<span className="label">Recipient</span>
					<button
						className="recipient-pill recipient-pill--lg"
						onClick={onPickRecipient}
					>
						{recipient ? (
							<>
								<span className="avatar" />
								{recipient.slice(0, 6)}…{recipient.slice(-4)}
							</>
						) : (
							<>Paste address</>
						)}
						<ChevronIcon />
					</button>
				</div>

				<div className="send-meta-row">
					<span className="label">Network fee</span>
					<GaslessFeeDisplay feeSol={feeSol} isLoading={isFeeLoading} />
				</div>
			</div>

			<button className="btn-primary" disabled={isDisabled} onClick={onSubmit}>
				{btnLabel()}
			</button>
		</div>
	);
}
